/**
 * Base Capability Configurations
 *
 * These provide default configs that genie personas can extend.
 * This allows for consistent behavior across similar genie types
 * while still allowing industry-specific customization.
 */

import { BaseCapability, GenieConfig } from '@/types/genie';

/**
 * Emergency Response - 24/7 urgent call handling
 */
export const emergencyResponseCapability: BaseCapability = {
  type: 'emergency-response',
  name: 'Emergency Response',
  description: 'Handles urgent after-hours calls, assesses severity, and dispatches or schedules appropriately.',
  defaultConfig: {
    afterHours: true,
    suggestedReplies: [
      "This is an emergency",
      "I need help now",
      "Schedule for later"
    ],
    collectFields: [
      { name: 'name', label: 'Your Name', type: 'text', required: true },
      { name: 'phone', label: 'Phone Number', type: 'phone', required: true },
      { name: 'address', label: 'Service Address', type: 'text', required: true },
      { name: 'issue', label: 'Describe the Issue', type: 'textarea', required: true },
    ],
    escalationRules: [
      {
        trigger: 'keyword',
        condition: 'emergency|urgent|flooding|burst|fire|gas',
        action: 'notify',
      }
    ],
    tags: ['emergency', '24/7', 'after-hours', 'urgent'],
  }
};

/**
 * Scheduling - Appointment booking
 */
export const schedulingCapability: BaseCapability = {
  type: 'scheduling',
  name: 'Scheduling',
  description: 'Books appointments, manages scheduling, and sends confirmations.',
  defaultConfig: {
    suggestedReplies: [
      "Book an appointment",
      "Check availability",
      "Reschedule"
    ],
    collectFields: [
      { name: 'name', label: 'Your Name', type: 'text', required: true },
      { name: 'phone', label: 'Phone Number', type: 'phone', required: true },
      { name: 'email', label: 'Email', type: 'email', required: false },
      { name: 'preferredDate', label: 'Preferred Date', type: 'date', required: true },
      { name: 'preferredTime', label: 'Preferred Time', type: 'select', required: true, options: ['Morning', 'Afternoon', 'Evening'] },
    ],
    tags: ['scheduling', 'appointments', 'booking'],
  }
};

/**
 * Quoting - Estimate/quote collection
 */
export const quotingCapability: BaseCapability = {
  type: 'quoting',
  name: 'Quoting',
  description: 'Collects project details and provides or requests quotes.',
  defaultConfig: {
    suggestedReplies: [
      "Get a quote",
      "Request estimate",
      "Pricing info"
    ],
    collectFields: [
      { name: 'name', label: 'Your Name', type: 'text', required: true },
      { name: 'phone', label: 'Phone Number', type: 'phone', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'projectDetails', label: 'Project Details', type: 'textarea', required: true },
    ],
    tags: ['quotes', 'estimates', 'pricing'],
  }
};

/**
 * Troubleshooting - Diagnostic walkthroughs
 */
export const troubleshootingCapability: BaseCapability = {
  type: 'troubleshooting',
  name: 'Troubleshooting',
  description: 'Walks users through basic diagnostics and fixes before dispatching service.',
  defaultConfig: {
    suggestedReplies: [
      "Something isn't working",
      "Need help fixing",
      "Talk to someone"
    ],
    tags: ['troubleshooting', 'diagnostics', 'help', 'support'],
  }
};

/**
 * Concierge - General assistance and recommendations
 */
export const conciergeCapability: BaseCapability = {
  type: 'concierge',
  name: 'Concierge',
  description: 'Provides general assistance, recommendations, and information.',
  defaultConfig: {
    suggestedReplies: [
      "I have a question",
      "Need recommendations",
      "Help me find"
    ],
    tags: ['concierge', 'assistance', 'help', 'recommendations'],
  }
};

/**
 * Lead Qualification - Qualifying and capturing leads
 */
export const leadQualificationCapability: BaseCapability = {
  type: 'lead-qualification',
  name: 'Lead Qualification',
  description: 'Welcomes visitors, qualifies their needs, and captures lead information.',
  defaultConfig: {
    suggestedReplies: [
      "I'm interested",
      "Tell me more",
      "Get started"
    ],
    collectFields: [
      { name: 'name', label: 'Your Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'phone', label: 'Phone Number', type: 'phone', required: false },
      { name: 'interest', label: 'What are you interested in?', type: 'textarea', required: true },
    ],
    tags: ['leads', 'qualification', 'welcome', 'sales'],
  }
};

/**
 * Follow-Up - Post-service check-ins and reviews
 */
export const followUpCapability: BaseCapability = {
  type: 'follow-up',
  name: 'Follow-Up',
  description: 'Checks in after service, collects feedback, and requests reviews.',
  defaultConfig: {
    suggestedReplies: [
      "Everything was great",
      "I have feedback",
      "Need more help"
    ],
    tags: ['follow-up', 'feedback', 'reviews', 'satisfaction'],
  }
};

/**
 * FAQ - Answering common questions
 */
export const faqCapability: BaseCapability = {
  type: 'faq',
  name: 'FAQ',
  description: 'Answers frequently asked questions and provides helpful information.',
  defaultConfig: {
    suggestedReplies: [
      "I have a question",
      "How does it work?",
      "What do you offer?"
    ],
    tags: ['faq', 'questions', 'help', 'information'],
  }
};

/**
 * Product Advisor - Product recommendations and comparisons
 */
export const productAdvisorCapability: BaseCapability = {
  type: 'product-advisor',
  name: 'Product Advisor',
  description: 'Helps customers find the right product through recommendations and comparisons.',
  defaultConfig: {
    suggestedReplies: [
      "Help me choose",
      "Compare options",
      "What do you recommend?"
    ],
    tags: ['products', 'recommendations', 'shopping', 'advisor'],
  }
};

/**
 * Order Tracking - Status and tracking updates
 */
export const orderTrackingCapability: BaseCapability = {
  type: 'order-tracking',
  name: 'Order Tracking',
  description: 'Provides status updates and tracking information.',
  defaultConfig: {
    suggestedReplies: [
      "Track my order",
      "Where is my delivery?",
      "Check status"
    ],
    collectFields: [
      { name: 'orderNumber', label: 'Order/Reference Number', type: 'text', required: true },
    ],
    tags: ['tracking', 'status', 'orders', 'delivery'],
  }
};

/**
 * Intake - Collecting project/request details
 */
export const intakeCapability: BaseCapability = {
  type: 'intake',
  name: 'Intake',
  description: 'Collects detailed information about projects or requests.',
  defaultConfig: {
    suggestedReplies: [
      "Start a project",
      "Submit a request",
      "Get started"
    ],
    collectFields: [
      { name: 'name', label: 'Your Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'phone', label: 'Phone Number', type: 'phone', required: true },
      { name: 'details', label: 'Project Details', type: 'textarea', required: true },
    ],
    tags: ['intake', 'projects', 'requests', 'details'],
  }
};

/**
 * Get a capability by type
 */
export const getCapability = (type: string): BaseCapability | undefined => {
  const capabilities: Record<string, BaseCapability> = {
    'emergency-response': emergencyResponseCapability,
    'scheduling': schedulingCapability,
    'quoting': quotingCapability,
    'troubleshooting': troubleshootingCapability,
    'concierge': conciergeCapability,
    'lead-qualification': leadQualificationCapability,
    'follow-up': followUpCapability,
    'faq': faqCapability,
    'product-advisor': productAdvisorCapability,
    'order-tracking': orderTrackingCapability,
    'intake': intakeCapability,
  };
  return capabilities[type];
};

/**
 * Merge capability defaults with custom config
 */
export const mergeWithCapability = (
  capabilityType: string,
  customConfig: Partial<GenieConfig>
): GenieConfig => {
  const capability = getCapability(capabilityType);
  const defaults = capability?.defaultConfig || {};

  return {
    firstMessage: customConfig.firstMessage || '',
    systemPrompt: customConfig.systemPrompt || '',
    ...defaults,
    ...customConfig,
    // Merge arrays instead of replacing
    suggestedReplies: customConfig.suggestedReplies || defaults.suggestedReplies,
    collectFields: customConfig.collectFields || defaults.collectFields,
    escalationRules: [
      ...(defaults.escalationRules || []),
      ...(customConfig.escalationRules || []),
    ],
    tags: [
      ...(defaults.tags || []),
      ...(customConfig.tags || []),
    ],
  };
};
